import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Search, Filter, Clock, DollarSign, MapPin } from 'lucide-react';
// Mock Data
const programs = [{
  id: 1,
  university: 'University of Manchester',
  program: 'MSc Data Science',
  country: 'UK',
  flag: '🇬🇧',
  duration: '1 Year',
  tuition: '£28,000',
  level: 'Postgraduate',
  image: 'https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
}, {
  id: 2,
  university: 'University of Toronto',
  program: 'BSc Computer Science',
  country: 'Canada',
  flag: '🇨🇦',
  duration: '4 Years',
  tuition: 'CAD 55,000/yr',
  level: 'Undergraduate',
  image: 'https://images.unsplash.com/photo-1517935706615-2717063c2225?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
}, {
  id: 3,
  university: 'Technical University of Munich',
  program: 'MSc Engineering',
  country: 'Germany',
  flag: '🇩🇪',
  duration: '2 Years',
  tuition: '€0 (Public)',
  level: 'Postgraduate',
  image: 'https://images.unsplash.com/photo-1467269204594-9661b134dd2b?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
}, {
  id: 4,
  university: 'Monash University',
  program: 'Bachelor of Business',
  country: 'Australia',
  flag: '🇦🇺',
  duration: '3 Years',
  tuition: 'AUD 45,000/yr',
  level: 'Undergraduate',
  image: 'https://images.unsplash.com/photo-1506973035872-a4ec16b8e8d9?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
}, {
  id: 5,
  university: 'New York University',
  program: 'MBA',
  country: 'USA',
  flag: '🇺🇸',
  duration: '2 Years',
  tuition: '$80,000/yr',
  level: 'Postgraduate',
  image: 'https://images.unsplash.com/photo-1550684848-fac1c5b4e853?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
}, {
  id: 6,
  university: 'University of Malaya',
  program: 'BSc Biotechnology',
  country: 'Malaysia',
  flag: '🇲🇾',
  duration: '3 Years',
  tuition: 'RM 25,000/yr',
  level: 'Undergraduate',
  image: 'https://images.unsplash.com/photo-1596422846543-75c6fc197f07?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
},
// Add more mock items to fill grid
{
  id: 7,
  university: 'Imperial College London',
  program: 'MSc Business Analytics',
  country: 'UK',
  flag: '🇬🇧',
  duration: '1 Year',
  tuition: '£34,000',
  level: 'Postgraduate',
  image: 'https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
}, {
  id: 8,
  university: 'UBC',
  program: 'BA Psychology',
  country: 'Canada',
  flag: '🇨🇦',
  duration: '4 Years',
  tuition: 'CAD 42,000/yr',
  level: 'Undergraduate',
  image: 'https://images.unsplash.com/photo-1517935706615-2717063c2225?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
}];
export function ProgramsPage() {
  const [filterLevel, setFilterLevel] = useState('All');
  const [filterCountry, setFilterCountry] = useState('All');
  const filteredPrograms = programs.filter(p => {
    const matchLevel = filterLevel === 'All' || p.level === filterLevel;
    const matchCountry = filterCountry === 'All' || p.country === filterCountry;
    return matchLevel && matchCountry;
  });
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="pt-20 bg-[#faf8f5] min-h-screen" data-id="element-481">
      {/* Header */}
      <div className="bg-[#1e3a5f] text-white py-16" data-id="element-482">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-483">
          <h1 className="text-4xl font-bold mb-4" data-id="element-484">Find Your Program</h1>
          <p className="text-gray-300 max-w-2xl" data-id="element-485">
            Browse through hundreds of courses from top universities around the
            world.
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8 relative z-10" data-id="element-486">
        <Card className="p-6 shadow-lg" data-id="element-487">
          <div className="flex flex-col md:flex-row gap-4 items-center" data-id="element-488">
            <div className="flex-1 w-full" data-id="element-489">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-490">
                Study Level
              </label>
              <select className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-[#1e3a5f] outline-none" value={filterLevel} onChange={e => setFilterLevel(e.target.value)} data-id="element-491">
                <option value="All" data-id="element-492">All Levels</option>
                <option value="Undergraduate" data-id="element-493">Undergraduate</option>
                <option value="Postgraduate" data-id="element-494">Postgraduate</option>
                <option value="Diploma" data-id="element-495">Diploma</option>
              </select>
            </div>
            <div className="flex-1 w-full" data-id="element-496">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-497">
                Destination
              </label>
              <select className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-[#1e3a5f] outline-none" value={filterCountry} onChange={e => setFilterCountry(e.target.value)} data-id="element-498">
                <option value="All" data-id="element-499">All Countries</option>
                <option value="UK" data-id="element-500">United Kingdom</option>
                <option value="USA" data-id="element-501">USA</option>
                <option value="Canada" data-id="element-502">Canada</option>
                <option value="Australia" data-id="element-503">Australia</option>
                <option value="Germany" data-id="element-504">Germany</option>
                <option value="Malaysia" data-id="element-505">Malaysia</option>
              </select>
            </div>
            <div className="w-full md:w-auto mt-auto pt-6" data-id="element-506">
              <Button fullWidth className="md:w-auto" data-id="element-507">
                <Search className="h-4 w-4 mr-2" data-id="element-508" />
                Search
              </Button>
            </div>
          </div>
        </Card>
      </div>

      {/* Results */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-id="element-509">
        <div className="mb-6 text-gray-600" data-id="element-510">
          Showing {filteredPrograms.length} programs
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-511">
          {filteredPrograms.map(program => <Card key={program.id} noPadding hoverEffect className="flex flex-col h-full" data-id="element-512">
              <div className="relative h-48 overflow-hidden" data-id="element-513">
                <img src={program.image} alt={program.university} className="w-full h-full object-cover transition-transform duration-500 hover:scale-110" data-id="element-514" />
                <div className="absolute top-4 right-4" data-id="element-515">
                  <Badge variant={program.level === 'Postgraduate' ? 'info' : 'success'} data-id="element-516">
                    {program.level}
                  </Badge>
                </div>
              </div>
              <div className="p-6 flex-1 flex flex-col" data-id="element-517">
                <div className="flex items-center text-sm text-gray-500 mb-2" data-id="element-518">
                  <span className="mr-2 text-lg" data-id="element-519">{program.flag}</span>
                  {program.country}
                </div>
                <h3 className="text-xl font-bold text-[#1e3a5f] mb-1" data-id="element-520">
                  {program.program}
                </h3>
                <p className="text-gray-600 font-medium mb-4" data-id="element-521">
                  {program.university}
                </p>

                <div className="mt-auto space-y-2 pt-4 border-t border-gray-100" data-id="element-522">
                  <div className="flex items-center text-sm text-gray-600" data-id="element-523">
                    <Clock className="h-4 w-4 mr-2 text-[#f59e0b]" data-id="element-524" />
                    {program.duration}
                  </div>
                  <div className="flex items-center text-sm text-gray-600" data-id="element-525">
                    <DollarSign className="h-4 w-4 mr-2 text-[#f59e0b]" data-id="element-526" />
                    {program.tuition}
                  </div>
                </div>

                <div className="mt-6" data-id="element-527">
                  <Button variant="outline" fullWidth size="sm" data-id="element-528">
                    Learn More
                  </Button>
                </div>
              </div>
            </Card>)}
        </div>
      </div>
    </motion.div>;
}